import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Agriculture as Farm,
  LocationOn,
  Phone,
  Email,
  Visibility,
  Business,
  CalendarToday,
  WaterDrop,
  Terrain,
  Info,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Close,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { farmsAPI, waterReservoirsAPI } from 'services/api';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Snackbar, FormControlLabel, Switch } from '@mui/material';

const FarmManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [farms, setFarms] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [editingFarm, setEditingFarm] = useState(null);
  const [viewingFarm, setViewingFarm] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [waterReservoirs, setWaterReservoirs] = useState([]);
  const [openWaterReservoirDialog, setOpenWaterReservoirDialog] = useState(false);
  const [editingWaterReservoir, setEditingWaterReservoir] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [waterReservoirFormData, setWaterReservoirFormData] = useState({
    reservoir_name: '',
    reservoir_type: 'storage',
    capacity_volume: '',
    capacity_area: '',
    treatment_facilities: false,
    treatment_type: '',
    water_source: '',
    is_functional: true,
    last_maintenance_date: '',
    certification_available: false,
    certification_file_path: '',
    notes: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    area: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    farming_type: 'shrimp',
    farm_type: 'owned',
    certification_status: 'none',
    registration_number: '',
    established_date: '',
    water_source: '',
    soil_type: '',
    description: '',
    geospatial_image_url: '',
  });

  useEffect(() => {
    if (user) {
      loadFarms();
    }
    
    // Check if action=add parameter is present to auto-open add dialog
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('action') === 'add') {
      setOpenDialog(true);
    }
  }, [user, location.search]);

  const loadFarms = async () => {
    if (!user) {
      console.warn('User not available, skipping farms load');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await farmsAPI.getAll();
      // Filter farms for current user
      const userFarms = response.data.filter(farm => farm.user_id === user.id);
      setFarms(userFarms);
    } catch (err) {
      console.error('Error loading farms:', err);
      setError('Failed to load farms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (farm) => {
    setViewingFarm(farm);
    setOpenDetailsDialog(true);
    if (farm.id) {
      loadWaterReservoirs(farm.id);
    }
  };

  const handleCloseDetailsDialog = () => {
    setOpenDetailsDialog(false);
    setViewingFarm(null);
    setWaterReservoirs([]);
  };

  const loadWaterReservoirs = async (farmId) => {
    try {
      const response = await waterReservoirsAPI.getByFarm(farmId);
      setWaterReservoirs(response.data || []);
    } catch (err) {
      console.error('Error loading water reservoirs:', err);
      setWaterReservoirs([]);
    }
  };

  const handleOpenWaterReservoirDialog = (reservoir = null) => {
    if (reservoir) {
      setEditingWaterReservoir(reservoir);
      setWaterReservoirFormData({
        reservoir_name: reservoir.reservoir_name || '',
        reservoir_type: reservoir.reservoir_type || 'storage',
        capacity_volume: reservoir.capacity_volume || '',
        capacity_area: reservoir.capacity_area || '',
        treatment_facilities: reservoir.treatment_facilities || false,
        treatment_type: reservoir.treatment_type || '',
        water_source: reservoir.water_source || '',
        is_functional: reservoir.is_functional !== undefined ? reservoir.is_functional : true,
        last_maintenance_date: reservoir.last_maintenance_date ? reservoir.last_maintenance_date.split('T')[0] : '',
        certification_available: reservoir.certification_available || false,
        certification_file_path: reservoir.certification_file_path || '',
        notes: reservoir.notes || '',
      });
    } else {
      setEditingWaterReservoir(null);
      setWaterReservoirFormData({
        reservoir_name: '',
        reservoir_type: 'storage',
        capacity_volume: '',
        capacity_area: '',
        treatment_facilities: false,
        treatment_type: '',
        water_source: '',
        is_functional: true,
        last_maintenance_date: '',
        certification_available: false,
        certification_file_path: '',
        notes: '',
      });
    }
    setOpenWaterReservoirDialog(true);
  };

  const handleCloseWaterReservoirDialog = () => {
    setOpenWaterReservoirDialog(false);
    setEditingWaterReservoir(null);
    setWaterReservoirFormData({
      reservoir_name: '',
      reservoir_type: 'storage',
      capacity_volume: '',
      capacity_area: '',
      treatment_facilities: false,
      treatment_type: '',
      water_source: '',
      is_functional: true,
      last_maintenance_date: '',
      certification_available: false,
      certification_file_path: '',
      notes: '',
    });
  };

  const handleSubmitWaterReservoir = async () => {
    try {
      if (!waterReservoirFormData.reservoir_name) {
        setSnackbar({
          open: true,
          message: 'Please fill in the reservoir name',
          severity: 'error'
        });
        return;
      }

      const submitData = {
        reservoir_name: waterReservoirFormData.reservoir_name,
        reservoir_type: waterReservoirFormData.reservoir_type,
        capacity_volume: waterReservoirFormData.capacity_volume ? parseFloat(waterReservoirFormData.capacity_volume) : null,
        capacity_area: waterReservoirFormData.capacity_area ? parseFloat(waterReservoirFormData.capacity_area) : null,
        treatment_facilities: waterReservoirFormData.treatment_facilities,
        treatment_type: waterReservoirFormData.treatment_type || null,
        water_source: waterReservoirFormData.water_source || null,
        is_functional: waterReservoirFormData.is_functional,
        last_maintenance_date: waterReservoirFormData.last_maintenance_date || null,
        certification_available: waterReservoirFormData.certification_available,
        certification_file_path: waterReservoirFormData.certification_file_path || null,
        notes: waterReservoirFormData.notes || null,
      };

      if (editingWaterReservoir) {
        await waterReservoirsAPI.update(viewingFarm.id, editingWaterReservoir.id, submitData);
        setSnackbar({ open: true, message: 'Water reservoir updated successfully', severity: 'success' });
      } else {
        await waterReservoirsAPI.create(viewingFarm.id, submitData);
        setSnackbar({ open: true, message: 'Water reservoir created successfully', severity: 'success' });
      }

      handleCloseWaterReservoirDialog();
      if (viewingFarm?.id) {
        loadWaterReservoirs(viewingFarm.id);
      }
    } catch (err) {
      console.error('Error saving water reservoir:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to save water reservoir. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleDeleteWaterReservoir = async (reservoirId) => {
    if (!window.confirm('Are you sure you want to delete this water reservoir? This action cannot be undone.')) {
      return;
    }

    try {
      await waterReservoirsAPI.delete(viewingFarm.id, reservoirId);
      setSnackbar({ open: true, message: 'Water reservoir deleted successfully', severity: 'success' });
      if (viewingFarm?.id) {
        loadWaterReservoirs(viewingFarm.id);
      }
    } catch (err) {
      console.error('Error deleting water reservoir:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to delete water reservoir. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleOpenDialog = (farm = null) => {
    if (farm) {
      setEditingFarm(farm);
      setFormData({
        name: farm.name || '',
        location: farm.location || '',
        area: farm.area ? farm.area.toString() : '',
        contact_person: farm.contact_person || '',
        contact_phone: farm.contact_phone || '',
        contact_email: farm.contact_email || '',
        farming_type: farm.farming_type || 'shrimp',
        farm_type: farm.farm_type || 'owned',
        certification_status: farm.certification_status || 'none',
        registration_number: farm.registration_number || '',
        established_date: farm.established_date ? farm.established_date.split('T')[0] : '',
        water_source: farm.water_source || '',
        soil_type: farm.soil_type || '',
        description: farm.description || '',
        geospatial_image_url: farm.geospatial_image_url || '',
      });
    } else {
      setEditingFarm(null);
      setFormData({
        name: '',
        location: '',
        area: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        farming_type: 'shrimp',
        certification_status: 'none',
      });
    }
    setOpenDialog(true);
  };

  const steps = ['Basic Information', 'Location & Area', 'Contact Details', 'Additional Information'];

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0: // Basic Information
        if (!formData.name.trim()) errors.name = 'Farm name is required';
        if (!formData.farm_type) errors.farm_type = 'Farm type is required';
        if (!formData.farming_type) errors.farming_type = 'Farming type is required';
        break;
      case 1: // Location & Area
        if (!formData.location.trim()) errors.location = 'Location is required';
        if (!formData.area || formData.area <= 0) errors.area = 'Valid area is required';
        break;
      case 2: // Contact Details
        if (!formData.contact_person.trim()) errors.contact_person = 'Contact person is required';
        if (!formData.contact_phone.trim()) errors.contact_phone = 'Contact phone is required';
        if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
          errors.contact_email = 'Valid email is required';
        }
        break;
      case 3: // Additional Information - optional fields
        break;
      default:
        break;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingFarm(null);
    setActiveStep(0);
    setFormErrors({});
    setFieldErrors({});
    setError(null);
    setFormData({
      name: '',
      location: '',
      area: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      farming_type: 'shrimp',
      farm_type: 'owned',
      certification_status: 'none',
      registration_number: '',
      established_date: '',
      water_source: '',
      soil_type: '',
      description: '',
      geospatial_image_url: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started:', { editingFarm: !!editingFarm, formData });
    
    // Clear previous errors
    setError(null);
    setFieldErrors({});
    
    // Client-side validation
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Farm name is required';
    }
    if (!formData.location.trim()) {
      errors.location = 'Farm location is required';
    }
    if (!formData.area || parseFloat(formData.area) <= 0) {
      errors.area = 'Valid farm area is required';
    }
    if (!formData.contact_person.trim()) {
      errors.contact_person = 'Contact person is required';
    }
    if (!formData.contact_phone.trim()) {
      errors.contact_phone = 'Contact phone is required';
    }
    if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
      errors.contact_email = 'Please enter a valid email address';
    }
    
    if (Object.keys(errors).length > 0) {
      console.log('Validation errors found:', errors);
      setFieldErrors(errors);
      return;
    }
    
    console.log('Validation passed, attempting API call...');
    
    try {
      const farmData = {
        ...formData,
        area: parseFloat(formData.area),
        // Ensure date is in correct format or null
        established_date: formData.established_date || null,
      };

      if (editingFarm) {
        console.log('Updating farm:', editingFarm.id, farmData);
        await farmsAPI.update(editingFarm.id, farmData);
        console.log('Farm update successful');
      } else {
        console.log('Creating new farm:', farmData);
        await farmsAPI.create(farmData);
        console.log('Farm creation successful');
      }

      handleCloseDialog();
      loadFarms();
    } catch (err) {
      console.error('Error saving farm:', err);
      
      // Handle API validation errors
      if (err.response && err.response.status === 422) {
        const errorData = err.response.data;
        
        // Handle different error formats
        if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            const errorDetail = errorData.detail;
            
            // Map backend validation errors to specific fields
            if (errorDetail.includes('Farm name')) {
              setFieldErrors({ name: errorDetail });
            } else if (errorDetail.includes('location')) {
              setFieldErrors({ location: errorDetail });
            } else if (errorDetail.includes('area')) {
              setFieldErrors({ area: errorDetail });
            } else if (errorDetail.includes('Contact person')) {
              setFieldErrors({ contact_person: errorDetail });
            } else if (errorDetail.includes('Contact phone')) {
              setFieldErrors({ contact_phone: errorDetail });
            } else {
              setError(errorDetail);
            }
          } else if (Array.isArray(errorData.detail)) {
            // Handle Pydantic validation errors (array format)
            const fieldMap = {};
            errorData.detail.forEach(error => {
              if (error.loc && error.loc.length > 0) {
                const fieldName = error.loc[error.loc.length - 1];
                fieldMap[fieldName] = error.msg || 'Invalid value';
              }
            });
            setFieldErrors(fieldMap);
          } else {
            setError('Validation error occurred. Please check your data.');
          }
        } else {
          setError('Failed to save farm. Please check your data and try again.');
        }
      } else {
        setError('Failed to save farm. Please check your data and try again.');
      }
    }
  };

  const handleDelete = async (farmId) => {
    if (window.confirm('Are you sure you want to delete this farm?')) {
      try {
        await farmsAPI.delete(farmId);
        loadFarms();
      } catch (err) {
        console.error('Error deleting farm:', err);
        setError('Failed to delete farm. Please try again.');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ===== KPI STATS ROW - Clean grid layout ===== */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(5, 1fr)', 
        gap: 2, 
        mb: 3 
      }}>
        {/* Total Farms */}
        <Card sx={{ 
          height: 100, 
          borderLeft: '4px solid #2196F3',
          '&:hover': { boxShadow: 3 }
        }}>
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                Total Farms
              </Typography>
              <Typography variant="h3" sx={{ color: '#2196F3', fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
                {farms.length}
              </Typography>
            </Box>
            <Farm sx={{ fontSize: 32, color: '#2196F3', opacity: 0.7 }} />
          </CardContent>
        </Card>

        {/* Active Farms */}
        <Card sx={{ 
          height: 100, 
          borderLeft: '4px solid #4CAF50',
          '&:hover': { boxShadow: 3 }
        }}>
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                Active
              </Typography>
              <Typography variant="h3" sx={{ color: '#4CAF50', fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
                {farms.filter(f => f.status === 'active').length}
              </Typography>
            </Box>
            <CheckCircle sx={{ fontSize: 32, color: '#4CAF50', opacity: 0.7 }} />
          </CardContent>
        </Card>

        {/* Total Area */}
        <Card sx={{ 
          height: 100, 
          borderLeft: '4px solid #00BCD4',
          '&:hover': { boxShadow: 3 }
        }}>
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                Total Area
              </Typography>
              <Typography variant="h4" sx={{ color: '#00BCD4', fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
                {farms.reduce((sum, farm) => sum + (farm.area || 0), 0).toFixed(1)}
              </Typography>
              <Typography variant="caption" color="text.secondary">hectares</Typography>
            </Box>
            <Terrain sx={{ fontSize: 32, color: '#00BCD4', opacity: 0.7 }} />
          </CardContent>
        </Card>

        {/* Certified */}
        <Card sx={{ 
          height: 100, 
          borderLeft: '4px solid #FF9800',
          '&:hover': { boxShadow: 3 }
        }}>
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                Certified
              </Typography>
              <Typography variant="h3" sx={{ color: '#FF9800', fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
                {farms.filter(f => f.certification_status !== 'none').length}
              </Typography>
            </Box>
            <Business sx={{ fontSize: 32, color: '#FF9800', opacity: 0.7 }} />
          </CardContent>
        </Card>

        {/* Ponds */}
        <Card sx={{ 
          height: 100, 
          borderLeft: '4px solid #9C27B0',
          '&:hover': { boxShadow: 3 }
        }}>
          <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 2.5 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
                Ponds
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="h3" sx={{ color: '#9C27B0', fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
                  {farms.reduce((sum, farm) => sum + (farm.active_ponds_count || 0), 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / {farms.reduce((sum, farm) => sum + (farm.total_ponds_count || 0), 0)}
                </Typography>
              </Box>
            </Box>
            <WaterDrop sx={{ fontSize: 32, color: '#9C27B0', opacity: 0.7 }} />
          </CardContent>
        </Card>
      </Box>

      {/* ===== FARMS TABLE - Professional styling ===== */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          px: 3, 
          py: 2, 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" fontWeight={600}>Your Farms</Typography>
          <Typography variant="body2" color="text.secondary">
            {farms.length} {farms.length === 1 ? 'farm' : 'farms'} registered
          </Typography>
        </Box>
        
        {farms.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <Farm sx={{ fontSize: 56, color: '#E0E0E0', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No farms registered yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start by adding your first shrimp farming location
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 2 }}
            >
              Add Your First Farm
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Farm</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Area</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Ponds</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {farms.map((farm) => (
                  <TableRow 
                    key={farm.id}
                    sx={{ 
                      '&:hover': { bgcolor: '#F5F9FF' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ 
                          width: 40, 
                          height: 40, 
                          borderRadius: 2, 
                          bgcolor: farm.status === 'active' ? '#E8F5E9' : '#F5F5F5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Farm sx={{ fontSize: 20, color: farm.status === 'active' ? '#4CAF50' : '#9E9E9E' }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1A1A1A' }}>
                            {farm.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {farm.contact_person}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <LocationOn sx={{ fontSize: 16, color: '#9E9E9E', mt: 0.25 }} />
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {farm.location}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {farm.area ? parseFloat(farm.area).toFixed(1) : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">hectares</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#2196F3' }}>
                          {farm.active_ponds_count || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          / {farm.total_ponds_count || 0}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={farm.status || 'inactive'}
                          size="small"
                          sx={{ 
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: farm.status === 'active' ? '#E8F5E9' : '#EEEEEE',
                            color: farm.status === 'active' ? '#2E7D32' : '#757575',
                            border: 'none'
                          }}
                        />
                        {farm.certification_status !== 'none' && (
                          <Chip
                            label={farm.certification_status}
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.65rem',
                              bgcolor: '#FFF3E0',
                              color: '#E65100'
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(farm)}
                            sx={{ 
                              color: '#2196F3',
                              '&:hover': { bgcolor: '#E3F2FD' }
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Farm">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(farm)}
                            sx={{ 
                              color: '#757575',
                              '&:hover': { bgcolor: '#F5F5F5' }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Farm">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(farm.id)}
                            sx={{ 
                              color: '#757575',
                              '&:hover': { bgcolor: '#FFEBEE', color: '#D32F2F' }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Simple & Professional Add/Edit Farm Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center">
            <Farm sx={{ mr: 2 }} />
            <Typography variant="h6" component="div">
              {editingFarm ? 'Edit Farm' : 'Register New Farm'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Basic Details Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Farm Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Coastal Aquaculture Farm"
                  required
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name ? String(fieldErrors.name) : ""}
                />
                <FormControl fullWidth required>
                  <InputLabel>Farm Type</InputLabel>
                  <Select
                    value={formData.farm_type}
                    onChange={(e) => setFormData({ ...formData, farm_type: e.target.value })}
                    label="Farm Type"
                  >
                    <MenuItem value="owned">Owned</MenuItem>
                    <MenuItem value="leased">Leased</MenuItem>
                    <MenuItem value="rented">Rented</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Location */}
              <TextField
                fullWidth
                label="Farm Location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Complete address including village, district, state"
                multiline
                rows={2}
                required
                error={!!fieldErrors.location}
                helperText={fieldErrors.location ? String(fieldErrors.location) : ""}
              />

              {/* Area and Type Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Total Area (acres)"
                  type="number"
                  step="0.1"
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  required
                  error={!!fieldErrors.area}
                  helperText={fieldErrors.area ? String(fieldErrors.area) : ""}
                />
                <FormControl fullWidth required>
                  <InputLabel>Farming Type</InputLabel>
                  <Select
                    value={formData.farming_type}
                    onChange={(e) => setFormData({ ...formData, farming_type: e.target.value })}
                    label="Farming Type"
                  >
                    <MenuItem value="shrimp">Shrimp</MenuItem>
                    <MenuItem value="fish">Fish</MenuItem>
                    <MenuItem value="mixed">Mixed</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Contact Details Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  required
                  error={!!fieldErrors.contact_person}
                  helperText={fieldErrors.contact_person ? String(fieldErrors.contact_person) : ""}
                />
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+91-9876543210"
                  required
                  error={!!fieldErrors.contact_phone}
                  helperText={fieldErrors.contact_phone ? String(fieldErrors.contact_phone) : ""}
                />
              </Box>

              {/* Email and Certification Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="farm@example.com"
                  helperText={fieldErrors.contact_email ? String(fieldErrors.contact_email) : "Optional: For notifications"}
                  error={!!fieldErrors.contact_email}
                />
                <FormControl fullWidth>
                  <InputLabel>Certification</InputLabel>
                  <Select
                    value={formData.certification_status}
                    onChange={(e) => setFormData({ ...formData, certification_status: e.target.value })}
                    label="Certification"
                  >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="organic">Organic</MenuItem>
                    <MenuItem value="aquaculture">Aquaculture</MenuItem>
                    <MenuItem value="bap">BAP</MenuItem>
                    <MenuItem value="asc">ASC</MenuItem>
                    <MenuItem value="haccp">HACCP</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Optional Details Row */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Registration Number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  placeholder="Government registration number"
                />
                <TextField
                  fullWidth
                  label="Established Date"
                  type="date"
                  value={formData.established_date}
                  onChange={(e) => setFormData({ ...formData, established_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* Optional Fields */}
              <TextField
                fullWidth
                label="Geospatial Image URL"
                value={formData.geospatial_image_url}
                onChange={(e) => setFormData({ ...formData, geospatial_image_url: e.target.value })}
                placeholder="Google Maps or satellite image URL"
                helperText="Optional: Link to aerial view of your farm"
              />
            </Box>
          </DialogContent>
          
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ minWidth: 120 }}
              onClick={() => console.log('UPDATE button clicked - Form data:', formData)}
            >
              {editingFarm ? 'Update' : 'Register'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* ===== FARM DETAILS DIALOG - Professional card-based layout ===== */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={handleCloseDetailsDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          px: 3, 
          py: 2.5, 
          background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: 2, 
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Farm sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>{viewingFarm?.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                <Chip 
                  label={viewingFarm?.status || 'inactive'} 
                  size="small"
                  sx={{ 
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: viewingFarm?.status === 'active' ? 'rgba(76,175,80,0.9)' : 'rgba(255,255,255,0.3)',
                    color: 'white'
                  }}
                />
                {viewingFarm?.certification_status !== 'none' && (
                  <Chip 
                    label={viewingFarm?.certification_status} 
                    size="small"
                    sx={{ 
                      height: 20,
                      fontSize: '0.7rem',
                      bgcolor: 'rgba(255,152,0,0.9)',
                      color: 'white'
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDetailsDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>
        
        <DialogContent sx={{ p: 3, bgcolor: '#F8FAFC' }}>
          {viewingFarm && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
              
              {/* LEFT COLUMN */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                
                {/* Farm Overview Card */}
                <Card sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                      Farm Overview
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Farm Type</Typography>
                        <Typography variant="body2" fontWeight={500}>{viewingFarm.farm_type || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Farming Type</Typography>
                        <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>{viewingFarm.farming_type || '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Total Area</Typography>
                        <Typography variant="body2" fontWeight={500}>{viewingFarm.area ? `${parseFloat(viewingFarm.area).toFixed(2)} ha` : '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Water Spread</Typography>
                        <Typography variant="body2" fontWeight={500}>{viewingFarm.water_spread_area ? `${parseFloat(viewingFarm.water_spread_area).toFixed(2)} ha` : '—'}</Typography>
                      </Box>
                      <Box sx={{ gridColumn: 'span 2' }}>
                        <Typography variant="caption" color="text.secondary">Established</Typography>
                        <Typography variant="body2" fontWeight={500}>{viewingFarm.established_date ? new Date(viewingFarm.established_date).toLocaleDateString() : '—'}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Contact Card */}
                <Card sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                      Contact
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="caption" fontWeight={600} color="primary">{viewingFarm.contact_person?.charAt(0)}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{viewingFarm.contact_person}</Typography>
                          <Typography variant="caption" color="text.secondary">Contact Person</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Phone sx={{ fontSize: 16, color: '#9E9E9E' }} />
                        <Typography variant="body2">{viewingFarm.contact_phone}</Typography>
                      </Box>
                      {viewingFarm.contact_email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: 16, color: '#9E9E9E' }} />
                          <Typography variant="body2">{viewingFarm.contact_email}</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>

              {/* RIGHT COLUMN */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                
                {/* Ponds Summary Card */}
                <Card sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                      Ponds Summary
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" fontWeight={700} color="primary">{viewingFarm.active_ponds_count || 0}</Typography>
                        <Typography variant="caption" color="text.secondary">Active</Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h3" fontWeight={700} color="text.secondary">{viewingFarm.total_ponds_count || 0}</Typography>
                        <Typography variant="caption" color="text.secondary">Total</Typography>
                      </Box>
                    </Box>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      fullWidth 
                      onClick={() => navigate('/farmer/ponds')}
                      sx={{ mt: 2, borderRadius: 2 }}
                    >
                      Manage Ponds
                    </Button>
                  </CardContent>
                </Card>

                {/* Location Card - Moved to Right Column */}
                <Card sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #E5E7EB', bgcolor: '#FAFAFA' }}>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                      Location
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <LocationOn sx={{ fontSize: 18, color: '#9E9E9E', mt: 0.25 }} />
                      <Typography variant="body2">{viewingFarm.location}</Typography>
                    </Box>
                    {viewingFarm.geospatial_image_url && (
                      <Button 
                        variant="text" 
                        size="small" 
                        href={viewingFarm.geospatial_image_url}
                        target="_blank"
                        sx={{ mt: 1.5, p: 0 }}
                      >
                        View on Map →
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Box>

              {/* WATER RESERVOIRS - Full Width */}
              <Box sx={{ gridColumn: 'span 2' }}>
                <Card sx={{ borderRadius: 2, border: '1px solid #E5E7EB' }}>
                  <Box sx={{ 
                    px: 2.5, 
                    py: 1.5, 
                    borderBottom: '1px solid #E5E7EB', 
                    bgcolor: '#FAFAFA',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WaterDrop sx={{ fontSize: 18, color: '#2196F3' }} />
                      <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}>
                        Water Reservoirs
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleOpenWaterReservoirDialog()}
                      sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Add Reservoir
                    </Button>
                  </Box>
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    {waterReservoirs.length === 0 ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <WaterDrop sx={{ fontSize: 40, color: '#E0E0E0', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          No water reservoirs found
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Reservoirs with treatment facilities are eligible for 5% premium discount
                        </Typography>
                      </Box>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Capacity</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Treatment</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary', textAlign: 'right' }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {waterReservoirs.map((reservoir) => (
                            <TableRow key={reservoir.id} sx={{ '&:hover': { bgcolor: '#F5F9FF' } }}>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{reservoir.reservoir_name}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{reservoir.reservoir_type || '—'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {reservoir.capacity_volume ? `${parseFloat(reservoir.capacity_volume).toFixed(0)} L` : 
                                   reservoir.capacity_area ? `${parseFloat(reservoir.capacity_area).toFixed(1)} m²` : '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={reservoir.treatment_facilities ? 'Yes' : 'No'} 
                                  size="small"
                                  sx={{ 
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: reservoir.treatment_facilities ? '#E8F5E9' : '#EEEEEE',
                                    color: reservoir.treatment_facilities ? '#2E7D32' : '#757575'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={reservoir.is_functional ? 'Active' : 'Inactive'} 
                                  size="small"
                                  sx={{ 
                                    height: 20,
                                    fontSize: '0.65rem',
                                    bgcolor: reservoir.is_functional ? '#E3F2FD' : '#FFEBEE',
                                    color: reservoir.is_functional ? '#1565C0' : '#C62828'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                  <IconButton size="small" onClick={() => handleOpenWaterReservoirDialog(reservoir)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleDeleteWaterReservoir(reservoir.id)} sx={{ color: '#D32F2F' }}>
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        {/* Footer */}
        <Box sx={{ 
          px: 3, 
          py: 2, 
          borderTop: '1px solid #E5E7EB',
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5
        }}>
          <Button 
            onClick={handleCloseDetailsDialog}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}
          >
            Close
          </Button>
          <Button 
            onClick={() => {
              handleCloseDetailsDialog();
              handleOpenDialog(viewingFarm);
            }}
            variant="contained"
            startIcon={<Edit />}
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 120 }}
          >
            Edit Farm
          </Button>
        </Box>
      </Dialog>

      {/* ===== WATER RESERVOIR DIALOG - Professional Layout ===== */}
      <Dialog 
        open={openWaterReservoirDialog} 
        onClose={handleCloseWaterReservoirDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden', maxHeight: '90vh' }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          px: 3, 
          py: 2, 
          background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WaterDrop sx={{ fontSize: 24 }} />
            <Typography variant="h6" fontWeight={600}>
              {editingWaterReservoir ? 'Edit Reservoir' : 'Add Water Reservoir'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseWaterReservoirDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 0, bgcolor: '#F8FAFC' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Section 1: Context Info */}
            <Box sx={{ px: 3, py: 2, bgcolor: '#EEF2FF', borderBottom: '1px solid #E0E7FF' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Farm sx={{ fontSize: 16, color: '#4F46E5' }} />
                <Typography variant="body2" color="#4F46E5" fontWeight={500}>
                  {viewingFarm?.name || 'Farm'}
                </Typography>
              </Box>
            </Box>

            {/* Section 2: Basic Details */}
            <Box sx={{ px: 3, py: 2.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
                Basic Details
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Reservoir Name"
                  value={waterReservoirFormData.reservoir_name || ''}
                  onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, reservoir_name: e.target.value })}
                  required
                  placeholder="e.g., Main Storage"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Reservoir Type</InputLabel>
                  <Select
                    value={waterReservoirFormData.reservoir_type}
                    label="Reservoir Type"
                    onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, reservoir_type: e.target.value })}
                    sx={{ bgcolor: 'white' }}
                  >
                    <MenuItem value="storage">Storage</MenuItem>
                    <MenuItem value="treatment">Treatment</MenuItem>
                    <MenuItem value="settling">Settling</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Water Source"
                  value={waterReservoirFormData.water_source || ''}
                  onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, water_source: e.target.value })}
                  placeholder="e.g., Borewell, Canal"
                  size="small"
                  sx={{ gridColumn: 'span 2', '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Section 3: Capacity (Optional) */}
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Capacity
                </Typography>
                <Chip label="Optional" size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#F3F4F6', color: '#6B7280' }} />
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Volume (Liters)"
                  type="number"
                  value={waterReservoirFormData.capacity_volume || ''}
                  onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, capacity_volume: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                />
                <TextField
                  fullWidth
                  label="Area (m²)"
                  type="number"
                  value={waterReservoirFormData.capacity_area || ''}
                  onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, capacity_area: e.target.value })}
                  inputProps={{ min: 0, step: 0.01 }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Section 4: Insurance & Eligibility - EMPHASIZED */}
            <Box sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Insurance Eligibility
                </Typography>
              </Box>
              
              {/* Premium Discount Card */}
              <Card sx={{ 
                mb: 2, 
                borderRadius: 2, 
                border: waterReservoirFormData.treatment_facilities && waterReservoirFormData.is_functional 
                  ? '2px solid #10B981' 
                  : '1px solid #E5E7EB',
                bgcolor: waterReservoirFormData.treatment_facilities && waterReservoirFormData.is_functional 
                  ? '#ECFDF5' 
                  : 'white'
              }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ 
                        color: waterReservoirFormData.treatment_facilities && waterReservoirFormData.is_functional 
                          ? '#059669' 
                          : '#6B7280'
                      }}>
                        {waterReservoirFormData.treatment_facilities && waterReservoirFormData.is_functional 
                          ? '✓ 5% Premium Discount Applicable' 
                          : 'Premium Discount'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {waterReservoirFormData.treatment_facilities && waterReservoirFormData.is_functional 
                          ? 'Treatment facilities verified and functional' 
                          : 'Enable treatment facilities to qualify'}
                      </Typography>
                    </Box>
                    {waterReservoirFormData.treatment_facilities && waterReservoirFormData.is_functional && (
                      <Chip 
                        label="-5%" 
                        size="small" 
                        sx={{ 
                          bgcolor: '#10B981', 
                          color: 'white', 
                          fontWeight: 700,
                          fontSize: '0.8rem'
                        }} 
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Insurance Toggle Fields */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid #E5E7EB'
                }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Treatment Facilities</Typography>
                    <Typography variant="caption" color="text.secondary">Required for premium discount</Typography>
                  </Box>
                  <Switch
                    checked={waterReservoirFormData.treatment_facilities}
                    onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, treatment_facilities: e.target.checked })}
                    color="success"
                  />
                </Box>

                {waterReservoirFormData.treatment_facilities && (
                  <TextField
                    fullWidth
                    label="Treatment Type"
                    value={waterReservoirFormData.treatment_type || ''}
                    onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, treatment_type: e.target.value })}
                    placeholder="e.g., UV, Chlorination, Filtration"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                  />
                )}

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid #E5E7EB'
                }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Currently Functional</Typography>
                    <Typography variant="caption" color="text.secondary">Reservoir is operational</Typography>
                  </Box>
                  <Switch
                    checked={waterReservoirFormData.is_functional}
                    onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, is_functional: e.target.checked })}
                    color="success"
                  />
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'white',
                  border: '1px solid #E5E7EB'
                }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Certification Available</Typography>
                    <Typography variant="caption" color="text.secondary">Official documentation</Typography>
                  </Box>
                  <Switch
                    checked={waterReservoirFormData.certification_available}
                    onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, certification_available: e.target.checked })}
                    color="primary"
                  />
                </Box>

                {waterReservoirFormData.certification_available && (
                  <TextField
                    fullWidth
                    label="Certification URL/Path"
                    value={waterReservoirFormData.certification_file_path || ''}
                    onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, certification_file_path: e.target.value })}
                    placeholder="Link to certification document"
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                  />
                )}
              </Box>
            </Box>

            <Divider />

            {/* Section 5: Maintenance & Notes */}
            <Box sx={{ px: 3, py: 2.5 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5, display: 'block' }}>
                Maintenance & Notes
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Last Maintenance Date"
                  type="date"
                  value={waterReservoirFormData.last_maintenance_date || ''}
                  onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, last_maintenance_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                />
                <TextField
                  fullWidth
                  label="Notes"
                  value={waterReservoirFormData.notes || ''}
                  onChange={(e) => setWaterReservoirFormData({ ...waterReservoirFormData, notes: e.target.value })}
                  multiline
                  rows={2}
                  placeholder="Additional notes (optional)"
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Fixed Footer */}
        <Box sx={{ 
          px: 3, 
          py: 2, 
          borderTop: '1px solid #E5E7EB',
          bgcolor: 'white',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.5
        }}>
          <Button 
            onClick={handleCloseWaterReservoirDialog}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitWaterReservoir} 
            variant="contained" 
            disabled={!waterReservoirFormData.reservoir_name}
            sx={{ borderRadius: 2, textTransform: 'none', minWidth: 120 }}
          >
            {editingWaterReservoir ? 'Update Reservoir' : 'Create Reservoir'}
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
};

export default FarmManagement;
